import React, { useMemo, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { Avatar, Button, Text, TextInput } from "react-native-paper";
import * as ImagePicker from "expo-image-picker";
import { useAppSelector } from "../../redux/store";
import { useTranslation } from "react-i18next";
import { update } from "../../services/firestoreServices";
import { USERS_COLLECTION } from "../../constants/collections";
import { deleteAvatar, uploadAvatar } from "../../services/storage";

interface EditProfileScreenProps {
  navigation: any;
}

export default function EditProfileScreen({
  navigation,
}: EditProfileScreenProps) {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.auth.user);
  const config = useAppSelector((state) => state.auth.config);
  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [selectedAvatarUri, setSelectedAvatarUri] = useState<string | null>(
    null,
  );
  const [removeAvatarRequested, setRemoveAvatarRequested] = useState(false);
  const [saving, setSaving] = useState(false);

  const isAvatarEnabled = useMemo(() => {
    const usersCollection = config.database.find(
      (collection) => collection.name === USERS_COLLECTION,
    );
    return (
      usersCollection?.fields.some((field) => field.name === "avatarUrl") ??
      false
    );
  }, [config.database]);

  const avatarPreviewUri = removeAvatarRequested
    ? ""
    : selectedAvatarUri || user.avatarUrl || "";

  const avatarLabel = `${firstName} ${lastName}`
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

  const handlePickAvatar = async () => {
    try {
      if (Platform.OS !== "web") {
        const permission =
          await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (!permission.granted) {
          alert(t("Gallery permission is required"));
          return;
        }
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets?.length > 0) {
        setSelectedAvatarUri(result.assets[0].uri);
        setRemoveAvatarRequested(false);
      }
    } catch (error) {
      console.error("Error picking avatar:", error);
      alert(t("Failed to pick file"));
    }
  };

  const handleRemoveAvatar = () => {
    setSelectedAvatarUri(null);
    setRemoveAvatarRequested(true);
  };

  const handleUpdateProfile = async () => {
    try {
      setSaving(true);

      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();
      const payload: Record<string, any> = {
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        fullName,
        fullNameSearch: fullName.toLowerCase(),
      };

      if (isAvatarEnabled) {
        if (selectedAvatarUri) {
          if (!user.company?.id) {
            throw new Error(t("No company selected"));
          }
          const avatarDownloadUrl = await uploadAvatar(
            selectedAvatarUri,
            user.company.id,
          );
          payload.avatarUrl = `${avatarDownloadUrl}${
            avatarDownloadUrl.includes("?") ? "&" : "?"
          }v=${Date.now()}`;
        } else if (removeAvatarRequested) {
          if (user.company?.id && user.avatarUrl) {
            try {
              await deleteAvatar(user.company.id);
            } catch (error) {
              console.warn("Failed to delete avatar from storage:", error);
            }
          }
          payload.avatarUrl = "";
        }
      }

      await update(USERS_COLLECTION, user.uid, {
        ...payload,
      });
      navigation.goBack();
    } catch (error) {
      console.error("Error updating profile:", error);
      alert((error as Error)?.message || t("An error occurred"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  return (
    <KeyboardAwareScrollView
      contentContainerStyle={styles.container}
      bottomOffset={20}
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.contentContainer}>
        <Text variant="titleLarge" style={styles.modalTitle}>
          {t("Change name")}
        </Text>
        {isAvatarEnabled && (
          <View style={styles.avatarSection}>
            {avatarPreviewUri ? (
              <Avatar.Image size={104} source={{ uri: avatarPreviewUri }} />
            ) : (
              <Avatar.Text size={104} label={avatarLabel || "U"} />
            )}
            <View style={styles.avatarActions}>
              <Button
                mode="contained-tonal"
                onPress={handlePickAvatar}
                disabled={saving}
              >
                {t("Add")}
              </Button>
              {!!avatarPreviewUri && (
                <Button
                  mode="text"
                  onPress={handleRemoveAvatar}
                  disabled={saving}
                >
                  {t("Delete")}
                </Button>
              )}
            </View>
          </View>
        )}
        <TextInput
          label={t("First name")}
          value={firstName}
          onChangeText={setFirstName}
          style={styles.input}
        />
        <TextInput
          label={t("Last name")}
          value={lastName}
          onChangeText={setLastName}
          style={styles.input}
        />
        <View style={styles.modalButtons}>
          <Button onPress={handleCancel} disabled={saving}>
            {t("Cancel")}
          </Button>
          <Button
            mode="contained"
            onPress={handleUpdateProfile}
            loading={saving}
          >
            {t("Save")}
          </Button>
        </View>
      </View>
    </KeyboardAwareScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: "transparent",
  },
  contentContainer: {
    maxWidth: 500,
    width: "100%",
    alignSelf: "center",
    padding: 20,
    margin: 20,
    borderRadius: 8,
    marginHorizontal: "auto",
  },
  modalTitle: {
    marginBottom: 20,
    textAlign: "center",
  },
  avatarSection: {
    alignItems: "center",
    marginBottom: 20,
    gap: 12,
  },
  avatarActions: {
    flexDirection: "row",
    gap: 8,
  },
  input: {
    marginBottom: 15,
  },
  modalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 10,
  },
});
